/**
 * Native-language preference.
 *
 * The user's native language is auto-detected from `navigator.language`
 * on first load and persisted to localStorage. Runtime translation was
 * removed alongside the Cloudflare Worker — Darija explanations now live
 * directly in the slide content.
 */

import { LANGUAGES, getLanguage, isSupportedLanguage } from './languages';

const STORAGE_KEY = 'jle.nativeLang';

type Listener = (lang: string) => void;
const listeners = new Set<Listener>();

let current: string = detectInitial();

function detectInitial(): string {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && isSupportedLanguage(saved)) return saved;
  } catch {
    /* localStorage may be blocked */
  }
  const nav = (navigator.language || 'en').toLowerCase().split('-')[0] ?? 'en';
  return isSupportedLanguage(nav) ? nav : 'en';
}

export function getNativeLanguage(): string {
  return current;
}

export function setNativeLanguage(lang: string): void {
  if (!isSupportedLanguage(lang) || lang === current) return;
  current = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* ignore */
  }
  for (const l of listeners) l(lang);
}

export function onNativeLanguageChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export { LANGUAGES, getLanguage };
